import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { VerificationGate } from "@/components/VerificationGate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, ListChecks, CheckCircle2, CreditCard, Wallet, Banknote, Copy, Loader2, Share2, Building2 } from "lucide-react";

const money = (n) => `R${Number(n || 0).toFixed(2)}`;
const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; } };
const CLS = { paid: "bg-emerald-100 text-emerald-700", started: "bg-amber-100 text-amber-700", registered: "bg-slate-100 text-slate-500" };

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid={`partner-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</span><span className={`w-9 h-9 rounded-md flex items-center justify-center ${accent || "bg-[#0B2447]/5"}`}><Icon className="w-4 h-4 text-[#0B2447]" /></span></div>
      <div className="font-display text-3xl font-bold text-[#0B2447] mt-3">{value}</div>
    </div>
  );
}

function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/partner/dashboard").then(({ data }) => setData(data)).catch(() => toast.error("Could not load your dashboard.")); }, []);
  if (!data) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#0B2447]" /></div>;
  const { profile, stats, customers, yva_commission_per_app } = data;
  const link = `${window.location.origin}/register?ref=${profile.ref_code}`;
  const copy = () => { navigator.clipboard.writeText(link); toast.success("Partner link copied!"); };
  return (
    <>
      <div className="flex items-center gap-2 text-[#0B2447]"><Building2 className="w-5 h-5 text-[#F5C32C]" /><span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Application Partner</span></div>
      <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0B2447] mt-1" data-testid="partner-title">Welcome, {profile.full_name || profile.email}</h1>
      <p className="text-slate-600 mt-2">You charge your customers <span className="font-semibold text-[#0B2447]">{money(profile.customer_price)}</span> per application. Your Varsity Apply keeps <span className="font-semibold text-[#0B2447]">{money(yva_commission_per_app)}</span> per paid application; you keep the rest.</p>

      <div className="mt-6 bg-white border border-[#0B2447]/20 rounded-xl p-5 shadow-sm" data-testid="partner-link-card">
        <div className="flex items-center gap-2 text-[#0B2447]"><Share2 className="w-4 h-4" /><span className="font-display font-bold">Your customer link</span></div>
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <code className="flex-1 bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5 text-sm text-slate-700 break-all" data-testid="partner-ref-link">{link}</code>
          <Button onClick={copy} data-testid="partner-copy-link" className="bg-[#0B2447] hover:bg-[#153461]"><Copy className="w-4 h-4 mr-1.5" /> Copy link</Button>
        </div>
        <p className="text-xs text-slate-400 mt-2">Anyone who registers through this link becomes your customer.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="partner-stats">
        <Stat icon={Users} label="Total Customers" value={stats.customers} />
        <Stat icon={ListChecks} label="Applications Started" value={stats.apps_started} accent="bg-[#F5C32C]/20" />
        <Stat icon={CheckCircle2} label="Applications Completed" value={stats.apps_completed} accent="bg-emerald-100" />
        <Stat icon={CreditCard} label="Successful Payments" value={stats.successful_payments} accent="bg-emerald-100" />
        <Stat icon={Wallet} label="Total Revenue" value={money(stats.revenue)} />
        <Stat icon={Banknote} label="YVA Commission" value={money(stats.yva_commission)} accent="bg-slate-100" />
        <Stat icon={Wallet} label="Partner Amount" value={money(stats.partner_amount)} accent="bg-[#F5C32C]/20" />
        <Stat icon={Banknote} label="Outstanding" value={money(stats.outstanding)} accent="bg-amber-100" />
      </div>

      <div className="mt-8 bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto" data-testid="partner-customers">
        <div className="p-4 border-b border-slate-100"><h3 className="font-display font-bold text-[#0B2447]">Your customers</h3></div>
        <Table>
          <TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>Registered</TableHead><TableHead className="text-center">Apps</TableHead><TableHead className="text-center">Paid</TableHead><TableHead className="text-center">Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {customers.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No customers yet — share your link!</TableCell></TableRow>}
            {customers.map((c, i) => (
              <TableRow key={i} data-testid={`partner-cust-${i}`}>
                <TableCell><div className="font-medium text-[#0B2447]">{c.student_name || "—"}</div><div className="text-xs text-slate-400">{c.student_email}</div></TableCell>
                <TableCell className="text-slate-500 text-sm">{fmtDate(c.registered_at)}</TableCell>
                <TableCell className="text-center">{c.applications}</TableCell>
                <TableCell className="text-center font-medium text-[#0B2447]">{c.paid_applications}</TableCell>
                <TableCell className="text-center font-semibold text-[#0B2447]">{money(c.amount_paid)}</TableCell>
                <TableCell><Badge className={`${CLS[c.status]} border-0 capitalize`}>{c.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

export default function Partner() {
  return (
    <div className="min-h-screen bg-[#F8F9FA]"><Header />
      <div className="max-w-6xl mx-auto px-6 py-10"><VerificationGate><Dashboard /></VerificationGate></div>
    </div>
  );
}
